import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_PAGE, DEFAULT_TOTAL_PAGES, OrderExtend, OrderStatus } from "@app/constants";
import OrderService from "@app/services/http/order.service";
import { openPortalDialog } from "@app/services/modal.service";
import OrderHistoryModal from "@app/components/order-history-modal";
import { Order, OrderSummary } from "@app/types";
import { formatDate, formatMoneyVN, getOrderEndTime } from "@app/utils/util";
import useObservable from "@core/hooks/use-observable.hook";
import { useT } from "@app/contexts/preferences.context";
import OrderStats from "./order-stats";
import { Cell, HistoryCell, NameCell, OrdersShell, Row, StateCell, useDebounced } from "./orders-shell";

type Filter = "" | OrderStatus;

const HEADERS = ["Máy", "Cấu hình", "Thời gian thuê", "Bắt đầu → Kết thúc", "Tổng tiền", "Trạng thái"];

/** Đơn thuê máy tính: OrderService.getOrders (thêm keyword, startFrom, startTo) */
function PcOrders() {
  const t = useT();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [orders, setOrders] = useState<Order[]>([]);
  const [count, setCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState<Filter>("");
  // "Xem đơn" ở Biến động số dư mở trang này với ?type=pc&q=<mã> để tìm sẵn đơn tương ứng
  const [params] = useSearchParams();
  const [q, setQ] = useState(() => ((params.get("type") || "pc") === "pc" ? params.get("q") || "" : ""));
  const keyword = useDebounced(q);
  const [startFrom, setStartFrom] = useState("");
  const [startTo, setStartTo] = useState("");
  const [summary, setSummary] = useState<OrderSummary>();

  const { subscribeUntilDestroy, subscribeOnce } = useObservable();

  useEffect(() => {
    subscribeOnce(OrderService.getOrderSummary("pc"), setSummary);
  }, []);

  useEffect(() => {
    subscribeUntilDestroy(
      OrderService.getOrders(page, "", "", "", status === "" ? undefined : status, { keyword, startFrom, startTo }),
      (res) => {
        setOrders(res?.data || []);
        setTotalPages(res?.total_page || DEFAULT_TOTAL_PAGES);
        setCount(res?.count || 0);
        setLoaded(true);
      }
    );
  }, [page, keyword, status, startFrom, startTo]);

  return (
    <>
      <OrderStats summary={summary} />
      <OrdersShell<Filter>
        search={{
          value: q,
          placeholder: "Tìm theo tên máy, mã đơn, IP…",
          onChange: (v) => {
            setQ(v);
            setPage(DEFAULT_PAGE);
          },
        }}
        dates={{
          from: startFrom,
          to: startTo,
          onChange: (from, to) => {
            setStartFrom(from);
            setStartTo(to);
            setPage(DEFAULT_PAGE);
          },
        }}
        tabs={{
          value: status,
          onChange: (v) => {
            setStatus(v);
            setPage(DEFAULT_PAGE);
          },
          items: [
            { value: "", label: "Tất cả", count: summary?.total },
            { value: OrderStatus.BUSY, label: "Đang hoạt động", count: summary?.active },
            { value: OrderStatus.FINISHED, label: "Đã kết thúc", count: summary?.ended },
          ],
        }}
        headers={HEADERS}
        loading={!loaded}
        empty={!orders.length}
        filtered={!!keyword || status !== "" || !!startFrom || !!startTo}
        page={page}
        totalPages={totalPages}
        count={count}
        onPageChange={setPage}
      >
        {orders.map((order) => {
          const pkg = order.image?.package;
          // quá giờ kết thúc mà server chưa chạy cron tắt máy (tối đa 5 phút) cũng coi là đã kết thúc
          const ended = order.status === OrderStatus.FINISHED || getOrderEndTime(order).valueOf() <= Date.now();

          return (
            <Row key={order._id} ended={ended}>
              <NameCell label={HEADERS[0]} name={order.image?.name || "—"} id={order._id} ip={order.remote_ext_ip} />
              <Cell label={HEADERS[1]}>
                <b className="text-fg">{pkg?.name || "—"}</b>
                {pkg && (
                  <small className="text-xs tabular-nums text-muted">
                    {formatMoneyVN(pkg.price_per_hour)}đ/{t("giờ")} · {formatMoneyVN(pkg.price_per_day)}đ/{t("ngày")}
                  </small>
                )}
              </Cell>
              <Cell label={HEADERS[2]} className="tabular-nums">
                <b className="text-fg">{order.rental_period}h</b>
                {!!order.extend_count && (
                  <small className="text-xs text-muted">
                    {t("Gia hạn")} {order.extend_count} {t("lần")}
                  </small>
                )}
                {order.after_exp === OrderExtend.YES && <small className="text-xs text-muted">{t("Có gia hạn")}</small>}
              </Cell>
              <Cell label={HEADERS[3]} className="o-d whitespace-nowrap tabular-nums">
                <span>{formatDate(order.time_boot)}</span>
                <span>{getOrderEndTime(order).format("DD/MM/YYYY HH:mm:ss")}</span>
              </Cell>
              <Cell label={HEADERS[4]} className="tabular-nums">
                <b className="text-fg">{formatMoneyVN(order.total_price || 0)}đ</b>
                {!!order.discount && (
                  <small className="text-xs text-ok">
                    {t("Chiết khấu")} {formatMoneyVN(order.discount)}đ
                  </small>
                )}
              </Cell>
              <StateCell label={HEADERS[5]} tone={ended ? "muted" : "ok"} text={ended ? "Kết thúc" : "Đang thuê"} />
              <HistoryCell onClick={() => openPortalDialog(OrderHistoryModal, { order_id: order._id })} />
            </Row>
          );
        })}
      </OrdersShell>
    </>
  );
}

export default PcOrders;
